import { useMemo, useState } from 'react';
import BotDamagePanel from '../../components/BotDamagePanel';
import api from '../../services/api';
import CityScreen from './CityScreen';
import { getWorkshopRepairOptions } from './cityUtils';
import { useCityContext } from './useCityContext';

export default function CityWorkshop() {
  const { loading, error, context, setContext, cityTypeLabel, reloadContext } = useCityContext();
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');

  const repairOptions = useMemo(() => (context.bot ? getWorkshopRepairOptions(context.bot) : []), [context.bot]);
  const damagedOptions = repairOptions.filter((option) => option.damage > 0);

  async function handleRepairPart(partKey) {
    try {
      setBusy(true);
      setMessage('');
      const res = await api.post('/robots/my-city-workshop/repair', { partKey });
      const cost = Number(res.data?.cost || 0);
      const integrity = res.data?.integrity || {};

      setContext((current) => {
        if (!current?.bot) return current;
        return {
          ...current,
          bot: {
            ...current.bot,
            ...integrity,
            money: Math.max(0, Number(current.bot.money || 0) - cost)
          }
        };
      });
      setMessage(res.data?.message
        ? `${res.data.message}${res.data.cost ? ` (Coste: ${res.data.cost})` : ''}`
        : 'Reparacion completada');
      void reloadContext(true).catch(() => {});
    } catch (err) {
      const details = err.response?.data?.details;
      setMessage([
        err.response?.data?.error || 'Error al reparar',
        details && details !== err.response?.data?.error ? details : ''
      ].filter(Boolean).join(' - '));
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <div className="min-h-screen bg-[#04060d] text-cyan-200 flex items-center justify-center">Cargando taller...</div>;
  if (error) return <div className="min-h-screen bg-[#04060d] text-rose-200 flex items-center justify-center">{error}</div>;

  return (
    <CityScreen
      context={context}
      cityTypeLabel={cityTypeLabel}
      title={`Taller - ${context.city?.name || 'Ciudad'}`}
      subtitle="Reparacion rapida de tu BOT"
    >
      {message && <div className="rounded-xl border border-cyan-500/30 bg-[#08101f] p-3 text-sm text-cyan-200">{message}</div>}

      <section className="rounded-xl border border-cyan-500/30 bg-[#08101f] p-5 space-y-5">
        <div>
          <h2 className="font-bold text-cyan-300">Reparacion rapida de tu BOT</h2>
          <p className="mt-1 text-sm text-slate-300/90">
            Repara solo la parte que necesites. Cada boton muestra el coste exacto segun el daño pendiente.
          </p>
        </div>

        <div className="rounded-xl border border-cyan-500/30 bg-[#060c18] p-4">
          <div className="text-xs uppercase tracking-[0.2em] text-slate-400">Oro total</div>
          <div className="mt-1 text-3xl font-bold text-cyan-200">{Number(context.bot?.money || 0)}</div>
        </div>

        <BotDamagePanel bot={context.bot} title="Daño del BOT" />

        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-cyan-200">Partes reparables</h3>
          {damagedOptions.length === 0 ? (
            <p className="text-sm text-emerald-300">El BOT ya esta completamente reparado.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {damagedOptions.map((part) => (
                <div key={part.key} className="rounded-xl border border-slate-700 bg-[#060c18] p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="font-semibold text-slate-100">{part.label}</div>
                      <div className="text-xs text-slate-400">
                        {part.current}/{part.max} | Dano: {part.damage}
                      </div>
                    </div>
                    <button
                      type="button"
                      disabled={busy || part.cost <= 0}
                      onClick={() => void handleRepairPart(part.key)}
                      className="rounded-lg border border-amber-400/60 bg-amber-500/10 px-4 py-2 text-sm text-amber-200 hover:bg-amber-500/20 disabled:opacity-50"
                    >
                      Reparar por {part.cost} oro
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </CityScreen>
  );
}
